<template>
  <div class="grid-order-documents">
    <div class="documents-group">
      <p class="title">
        {{ $t('viewOrder.documents_grid.confirmation_title') }}
      </p>

      <div
        v-for="document in confirmations"
        :key="document.id"
        class="document"
        v-on:click="downloadDocument(document)">
        <button
          class="button download-button"
          :class="[{ submitting: document.loading }]"
          :disabled="document.loading"></button>
        <span>
          {{ getOrderDocumentDownloadButtonText(document, $t, userLocale) }}
        </span>
      </div>
    </div>

    <div v-if="showPackingSlips" class="documents-group">
      <p class="title">
        <label class="title">{{ $t('viewOrder.documents_grid.packing_slips_title') }}</label>
      </p>

      <div v-for="packingSlip in packingSlips" :key="packingSlip.id" class="document">
        <button
          class="button download-button"
          :class="[{ submitting: packingSlip.loading }]"
          :disabled="packingSlip.loading"
          v-on:click="downloadDocument(packingSlip)"></button>
        <span>
          {{ getOrderDocumentDownloadButtonText(packingSlip, $t, userLocale) }}
        </span>
      </div>
    </div>

    <div v-if="invoices.length" class="documents-group">
      <p class="title">
        {{ $t('viewOrder.documents_grid.invoices_title') }}
      </p>

      <div v-for="invoice in invoices" :key="invoice.id" class="document" v-on:click="downloadDocument(invoice)">
        <button
          class="button download-button"
          :class="[{ submitting: invoice.loading }]"
          :disabled="invoice.loading"></button>
        <span>
          {{ getOrderDocumentDownloadButtonText(invoice, $t, userLocale) }}
        </span>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { useDocumentHelpers } from '@/composables/document-helpers';
import { ordersMixins } from '@/mixins/orders.mixins';
import { utilsMixins } from '@/mixins/utils.mixins';
import { OrderDocument } from '@/models/order-document.model';
import { OrderDocumentService } from '@/services/order-document-service';
import { storeToRefs } from 'pinia';
import { defineComponent, reactive } from 'vue';
import { useLocale } from '../../composables/locale';
import { OrderDocumentType } from '../../models/order-document-type.enum';
import type { Order } from '../../models/order.model';
import { useUserStore } from '../../stores/user';

export default defineComponent({
  name: 'grid-order-document',
  props: {
    order: {
      type: Object,
      default: null,
    },
  },
  setup() {
    const { openPDF } = useDocumentHelpers();

    return {
      openPDF,
    };
  },
  data() {
    const { userLocale } = useLocale();
    const { selectedAccountId } = storeToRefs(useUserStore());

    return {
      orderDocumentService: new OrderDocumentService(),
      documents: new Array<OrderDocument>(),
      accountId: selectedAccountId,
      userLocale,
    };
  },
  computed: {
    confirmations(): OrderDocument[] {
      return this.documents
        .filter(
          (document) =>
            document.orderDocumentType?.toLocaleLowerCase() === OrderDocumentType.OrderConfirmation.toLocaleLowerCase(),
        )
        .sort(this.documentSorter);
    },
    packingSlips(): OrderDocument[] {
      return this.documents
        .filter(
          (document) =>
            document.orderDocumentType?.toLocaleLowerCase() === OrderDocumentType.PackingSlip.toLocaleLowerCase(),
        )
        .filter((document) => document.isReadyForDownload)
        .sort(this.documentSorter);
    },
    invoices(): OrderDocument[] {
      return this.documents
        .filter(
          (document) =>
            document.orderDocumentType?.toLocaleLowerCase() === OrderDocumentType.Invoice.toLocaleLowerCase(),
        )
        .sort(this.documentSorter);
    },
    showPackingSlips(): boolean {
      return this.packingSlips.length > 0;
    },
  },
  beforeMount() {
    this.documents = this.getDocuments();
  },
  methods: {
    ...ordersMixins,
    ...utilsMixins,
    async downloadDocument(document: OrderDocument) {
      if (document.loading) {
        return;
      }

      document.loading = true;

      const index = this.documents.indexOf(document);

      this.documents = reactive(this.documents);
      this.documents[index] = document;

      const validation = await this.orderDocumentService.get(
        this.order.orderNumber,
        document.id,
        this.accountId,
        this.getDocumentType(document.orderDocumentType),
      );

      if (validation.isValid() && validation.model?.base64Body) {
        this.openPDF(validation.model?.base64Body, document.id, document.orderNumber);
      } else {
        this.$snotify.warning(this.$t('documentsCheckout.document_is_not_available'));
      }

      document.loading = false;
      this.documents[index] = document;
    },
    documentSorter(a: OrderDocument, b: OrderDocument): number {
      if (a.createdDate && b.createdDate) {
        return a.createdDate.localeCompare(b.createdDate);
      } else if (a.packingSlipDate && b.packingSlipDate) {
        return a.packingSlipDate.toString().localeCompare(b.packingSlipDate.toString());
      } else {
        return a.id.localeCompare(b.id);
      }
    },
    getDocuments(): OrderDocument[] {
      return this.getOrderHeaderDocuments(this.order as Order, this.$t, this.userLocale);
    },
  },
});
</script>

<style scoped lang="scss">
.grid-order-documents {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  width: 100%;

  &:hover {
    background: inherit;
  }

  .columnsContainer {
    display: block;
    // The foloowing style can be used to split the row into two equal parts for double column documents
    //   display: flex;

    //   @include breakpoint(untilxlarge) {
    //     flex-direction: column;
    //   }

    //   .column {
    //     display: flex;
    //     flex-direction: column;
    //     align-items: flex-start;
    //     flex-basis: 50%;
    //   }
  }

  .document {
    display: flex;
    position: relative;
    transition: background-color 250ms ease;
    border-radius: 4px;
    cursor: pointer;
    font-size: 14px;
    margin-bottom: 10px;
    justify-content: flex-start;
    align-items: flex-start;

    .form.fixed .fields .field & span {
      line-height: normal;
      text-align: left;
    }
  }

  .documents-group {
    margin: 10px 0;

    &:first-child {
      margin-top: 20px;
    }

    &:last-child {
      margin-bottom: 0px;
    }

    .title {
      margin: 0 !important;
      padding-bottom: 6px;
    }
  }

  .download-button {
    padding: 0;
    height: 22px;
    min-width: auto;
    width: 22px;
    margin-right: 10px;
    margin-top: 2px;
  }

  .button.download-button.submitting {
    background-position: 50% center;
    padding: 0 12px;
    margin: -2px 8px 0 0;
  }
}
</style>
